import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useTranslation } from "../translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("validating");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const validate = async () => {
      if (!token) { setStatus("invalid"); return; }
      try {
        const { data } = await axios.get(`${API}/reset-password/validate`, { params: { token } });
        if (data.valid) { setEmail(data.email); setStatus("form"); }
        else setStatus("invalid");
      } catch {
        setStatus("invalid");
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) { setError(t("passwordMinLength")); return; }
    if (newPassword !== confirm) { setError(t("passwordsDontMatch")); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/reset-password`, { token, new_password: newPassword });
      setStatus("done");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("errorUnknown"));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center px-4">
      <div data-testid="reset-password-page" className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-green-100 p-8">
        <div className="flex items-center gap-2 justify-center mb-6">
          <span className="text-2xl font-bold text-green-800">HeveaTalent</span>
        </div>

        {status === "validating" && (
          <p className="text-center text-gray-500">...</p>
        )}

        {status === "invalid" && (
          <div className="text-center space-y-4">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" />
            <p data-testid="invalid-link-message" className="text-gray-700">{t("invalidResetLink")}</p>
            <Button data-testid="back-home-btn" className="w-full" onClick={() => navigate("/")}>
              {t("backToLogin")}
            </Button>
          </div>
        )}

        {status === "form" && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-center mb-2">
              <KeyRound className="w-10 h-10 text-green-700 mx-auto mb-2" />
              <h1 className="text-xl font-bold text-gray-900">{t("forgotPasswordTitle")}</h1>
              <p className="text-sm text-gray-500 mt-1">{email}</p>
            </div>
            <div>
              <Label htmlFor="rp-new">{t("newPasswordLabel")}</Label>
              <Input
                id="rp-new"
                data-testid="page-new-password-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="rp-confirm">{t("confirmPasswordLabel")}</Label>
              <Input
                id="rp-confirm"
                data-testid="page-confirm-password-input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>
            {error && <p data-testid="page-reset-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="page-reset-submit-btn" type="submit" className="w-full" disabled={loading}>
              {loading ? t("resetting") : t("resetPasswordBtn")}
            </Button>
          </form>
        )}

        {status === "done" && (
          <div className="text-center space-y-4">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
            <p data-testid="reset-success-message" className="text-gray-700">{t("passwordResetSuccess")}</p>
            <Button data-testid="go-login-btn" className="w-full" onClick={() => navigate("/")}>
              {t("backToLogin")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
